
import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { QuizResultsData, Question } from '@/types/quiz';

export interface QuizAnalysis {
  strongTopics: string[];
  weakTopics: string[];
  averageScore: number;
  timePerformance: 'good' | 'average' | 'needs_improvement';
  consistentErrors: string[];
  recommendedFocus: string[];
  performanceByTopic: Record<string, number>;
  conceptMastery: Record<string, { score: number; confidence: number; attempts: number }>;
  mistakePatterns: { pattern: string; frequency: number; examples: string[] }[];
  responseTimeAnalysis: { fast: string[]; slow: string[] };
}

export function useQuizAnalysis(subject?: string) {
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const analyzeQuizHistory = async (userId: string): Promise<QuizAnalysis | null> => {
    try {
      setIsAnalyzing(true);
      
      // Get recent quiz results for the subject
      const { data: quizResults, error: quizError } = await supabase
        .from('quiz_results')
        .select('*')
        .eq('user_id', userId)
        .eq('subject', subject)
        .order('created_at', { ascending: false })
        .limit(10);

      if (quizError) throw quizError;

      // Get detailed question history
      const { data: questionHistory, error: historyError } = await supabase
        .from('user_quiz_history')
        .select('*')
        .eq('user_id', userId)
        .eq('subject', subject)
        .order('answered_at', { ascending: false })
        .limit(50);

      if (historyError) throw historyError;

      // Analyze strengths and weaknesses
      const topicPerformance = new Map<string, { correct: number; total: number; time: number[]; }>();
      const errorPatterns = new Map<string, { count: number; examples: string[] }>();
      const conceptData = new Map<string, { score: number; confidence: number; attempts: number }>();
      
      // Initialize performance by topic object
      const performanceByTopic: Record<string, number> = {};
      
      // Track response times
      const responseTimes = new Map<string, number[]>();
      
      questionHistory?.forEach(history => {
        const topic = history.category || 'general';
        
        // Initialize if needed
        if (!topicPerformance.has(topic)) {
          topicPerformance.set(topic, { correct: 0, total: 0, time: [] });
        }
        
        const current = topicPerformance.get(topic)!;
        
        current.total++;
        if (history.is_correct) {
          current.correct++;
        } else {
          // Track error patterns
          const errorKey = `${topic}-${history.question_text.substring(0, 50)}`;
          const currentError = errorPatterns.get(errorKey) || { count: 0, examples: [] };
          currentError.count++;
          currentError.examples.push(history.question_text);
          errorPatterns.set(errorKey, currentError);
        }
        
        // Track response time if available
        if (history.metadata?.time_taken) {
          current.time.push(history.metadata.time_taken);
          
          // Also track by concept if available
          if (history.metadata?.concepts) {
            const concepts = Array.isArray(history.metadata.concepts) 
              ? history.metadata.concepts 
              : [history.metadata.concepts];
              
            concepts.forEach(concept => {
              if (!responseTimes.has(concept)) {
                responseTimes.set(concept, []);
              }
              responseTimes.get(concept)?.push(history.metadata.time_taken);
              
              // Update concept mastery
              const conceptKey = `${topic}:${concept}`;
              const currentConcept = conceptData.get(conceptKey) || { 
                score: 0, 
                confidence: 0, 
                attempts: 0 
              };
              
              currentConcept.attempts++;
              if (history.is_correct) {
                currentConcept.score += 1;
              }
              // Estimate confidence based on response time
              const avgTime = 30; // baseline average time in seconds
              const timeFactor = history.metadata.time_taken < avgTime ? 1 : (avgTime / history.metadata.time_taken);
              currentConcept.confidence = (currentConcept.confidence + (history.is_correct ? timeFactor : 0)) / 2;
              
              conceptData.set(conceptKey, currentConcept);
            });
          }
        }
        
        topicPerformance.set(topic, current);
      });

      // Calculate strong and weak topics
      const strongTopics: string[] = [];
      const weakTopics: string[] = [];
      const fastResponseTopics: string[] = [];
      const slowResponseTopics: string[] = [];
      
      topicPerformance.forEach((performance, topic) => {
        const score = (performance.correct / performance.total) * 100;
        performanceByTopic[topic] = score;
        
        if (score >= 70) {
          strongTopics.push(topic);
        } else if (score < 50) {
          weakTopics.push(topic);
        }
        
        // Analyze response times
        if (performance.time.length > 0) {
          const avgTime = performance.time.reduce((sum, time) => sum + time, 0) / performance.time.length;
          if (avgTime < 15) { // assuming 15 seconds is fast
            fastResponseTopics.push(topic);
          } else if (avgTime > 45) { // assuming 45 seconds is slow
            slowResponseTopics.push(topic);
          }
        }
      });

      // Calculate average score
      const averageScore = quizResults?.reduce((sum, quiz) => sum + quiz.score, 0) || 0;
      const avgScore = quizResults?.length ? averageScore / quizResults.length : 0;

      // Format concept mastery data
      const conceptMastery: Record<string, { score: number; confidence: number; attempts: number }> = {};
      conceptData.forEach((data, key) => {
        conceptMastery[key] = {
          score: data.attempts > 0 ? (data.score / data.attempts) * 100 : 0,
          confidence: data.confidence * 100,
          attempts: data.attempts
        };
      });

      // Identify consistent errors (appeared more than once)
      const mistakePatterns = Array.from(errorPatterns.entries())
        .filter(([_, data]) => data.count > 1)
        .map(([error, data]) => ({
          pattern: error.split('-')[0],
          frequency: data.count,
          examples: data.examples.slice(0, 3) // limit to 3 examples
        }));

      // Generate recommended focus areas
      const recommendedFocus = [...new Set([...weakTopics, ...slowResponseTopics])];
      
      // Identify consistent error patterns
      const consistentErrors = Array.from(new Set(mistakePatterns.map(p => p.pattern)));

      return {
        strongTopics,
        weakTopics,
        averageScore: avgScore,
        timePerformance: avgScore > 70 ? 'good' : avgScore > 50 ? 'average' : 'needs_improvement',
        consistentErrors,
        recommendedFocus,
        performanceByTopic,
        conceptMastery,
        mistakePatterns,
        responseTimeAnalysis: {
          fast: fastResponseTopics,
          slow: slowResponseTopics
        }
      };
    } catch (error) {
      console.error('Error analyzing quiz history:', error);
      return null;
    } finally {
      setIsAnalyzing(false);
    }
  };

  return {
    isAnalyzing,
    analyzeQuizHistory
  };
}
